import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Store, Plus, Trash2, Building, Warehouse, Edit3, AlertTriangle } from "lucide-react";

const TYPE_LABEL = { head_office: "Head Office", branch: "Retail Branch", retail_showroom: "Retail Showroom", warehouse: "Warehouse", factory: "Factory" };

const empty = { code: "", name: "", type: "branch", city: "", state: "", pincode: "", gstin: "", phone: "", email: "", address_line1: "" };

const TYPE_ICON = { head_office: Building, branch: Store, retail_showroom: Store, warehouse: Warehouse };

export default function BranchesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [typeWarn, setTypeWarn] = useState(null); // { message, usage, from, to }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/branches"); setItems(data); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (confirmTypeChange = false) => {
    if (!form.code || !form.name) return toast.error("Code and name required");
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`/branches/${editId}${confirmTypeChange ? "?confirm_type_change=true" : ""}`, form);
        toast.success(confirmTypeChange ? "Branch type changed (audited)" : "Branch updated");
      } else {
        await api.post("/branches", form);
        toast.success("Branch created");
      }
      setOpen(false); setForm(empty); setEditId(null); setTypeWarn(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail_obj;
      if (e?.response?.status === 409 && d?.code === "type_change_needs_confirmation") setTypeWarn(d);
      else toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const edit = (b) => {
    setEditId(b.id);
    setForm({
      code: b.code || "", name: b.name || "", type: b.type || "branch",
      city: b.city || "", state: b.state || "", pincode: b.pincode || "",
      gstin: b.gstin || "", phone: b.phone || "", email: b.email || "",
      address_line1: b.address_line1 || "",
    });
    setOpen(true);
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete branch ${b.name}?`)) return;
    try { await api.delete(`/branches/${b.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggle = async (b) => {
    await api.patch(`/branches/${b.id}`, { is_active: !b.is_active });
    load();
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Multi-branch</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Branches & Warehouses</h1>
          <p className="text-sm text-[#8B7355] mt-1">Every showroom / factory / warehouse operates from its own login and inventory.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); setForm(empty); setTypeWarn(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="branch-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New branch
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="font-[Outfit]">{editId ? "Edit branch" : "New branch"}</DialogTitle>
              <DialogDescription>Branch codes are used in POS invoice numbers, e.g. BMF/POS/MAIN/25/0001.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div><Label>Code *</Label><Input data-testid="branch-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="MAIN" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="branch-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="head_office">Head Office</SelectItem>
                    <SelectItem value="branch">Retail Branch</SelectItem>
                    <SelectItem value="retail_showroom">Retail Showroom</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Name *</Label><Input data-testid="branch-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main Store" /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} /></div>
              <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>Pincode</Label><Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
              <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            {typeWarn ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="branch-type-warning">
                <div className="flex items-start gap-2 text-sm text-amber-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">Change type {TYPE_LABEL[typeWarn.from] || typeWarn.from} → {TYPE_LABEL[typeWarn.to] || typeWarn.to}?</div>
                    <div className="mt-1">{typeWarn.message}</div>
                    <ul className="list-disc ml-4 mt-1 text-xs" data-testid="branch-type-warning-usage">
                      <li>{typeWarn.usage.stock_products} product(s) in stock · {typeWarn.usage.stock_qty} units</li>
                      <li>{typeWarn.usage.sales_count} sale(s) · {typeWarn.usage.dispatches_count} dispatch(es) · {typeWarn.usage.users_count} user login(s)</li>
                    </ul>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setTypeWarn(null)} disabled={saving} data-testid="branch-type-warning-cancel">Keep current type</Button>
                  <Button onClick={() => submit(true)} disabled={saving} data-testid="branch-type-warning-confirm" className="bg-amber-600 hover:bg-amber-700 text-white">
                    {saving ? "Saving…" : "Yes, change type"}
                  </Button>
                </div>
              </div>
            ) : (
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={() => submit(false)} disabled={saving} data-testid="branch-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">{editId ? "Save changes" : "Create"}</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b) => {
          const Icon = TYPE_ICON[b.type] || Store;
          return (
            <div key={b.id} className="bg-white border border-[#EAE5D9] rounded-lg p-4" data-testid={`branch-card-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-md bg-[#FEEBC8] text-[#C05621] flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => edit(b)} data-testid={`branch-edit-${b.id}`} className="w-8 h-8 rounded border border-[#EAE5D9] flex items-center justify-center hover:border-[#C05621]"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(b)} data-testid={`branch-delete-${b.id}`} className="w-8 h-8 rounded border border-[#EAE5D9] flex items-center justify-center text-red-500 hover:border-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="mt-3">
                <div className="uc-label">{(b.type || "").replace(/_/g, " ")}</div>
                <div className="font-semibold text-lg tracking-tight mt-0.5">{b.name}</div>
                <div className="text-xs text-[#8B7355]">Code: <span className="font-mono">{b.code}</span></div>
              </div>
              <div className="mt-3 text-xs text-[#5C4033] space-y-0.5">
                {b.city && <div>{b.city}, {b.state}</div>}
                {b.gstin && <div>GSTIN: <span className="font-mono">{b.gstin}</span></div>}
                {b.phone && <div>{b.phone}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-[#EAE5D9] flex items-center justify-between">
                <Badge variant={b.is_active ? "default" : "outline"} className={b.is_active ? "bg-emerald-500 text-white" : ""}>
                  {b.is_active ? "Active" : "Inactive"}
                </Badge>
                <Switch checked={b.is_active !== false} onCheckedChange={() => toggle(b)} data-testid={`branch-toggle-${b.id}`} />
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 text-[#8B7355]">No branches yet.</div>
        )}
      </div>
    </div>
  );
}
